import { Link } from "react-router-dom";
import GuideLayout from "@/components/guides/GuideLayout";

const faqItems = [
  {
    question: "What is Gedarel?",
    answer:
      "Gedarel is a combined contraceptive pill made by Gedeon Richter. It comes in two strengths: Gedarel 30/150 contains ethinylestradiol 30 micrograms and desogestrel 150 micrograms, and Gedarel 20/150 contains ethinylestradiol 20 micrograms with the same desogestrel dose. You take one tablet a day for 21 days, then have a 7 day break.",
  },
  {
    question: "What is the difference between Gedarel 20/150 and 30/150?",
    answer:
      "The only difference is the amount of oestrogen. Gedarel 20/150 has 20 micrograms of ethinylestradiol and Gedarel 30/150 has 30 micrograms. Both contain desogestrel 150 micrograms and are taken the same way. Your pharmacist will talk through which strength suits you, especially if you are switching from another pill.",
  },
  {
    question: "Is Gedarel the same as Marvelon?",
    answer:
      "Gedarel 30/150 contains the same hormones at the same doses as Marvelon: ethinylestradiol 30 micrograms and desogestrel 150 micrograms. Gedarel 20/150 matches Mercilon. The makers and the inactive ingredients differ, but the medicine is the same, so the dose, regimen and missed pill rules match.",
  },
  {
    question: "Can I get Gedarel free on the NHS?",
    answer:
      "Yes, if it is suitable for you. Combined pills are free on the NHS from a GP, a sexual health clinic, or a pharmacy providing the NHS Pharmacy Contraception Service. With Contraception Direct, the consultation, the pill and the delivery are free. A recent blood pressure reading is needed, and supply depends on the pharmacist's assessment.",
  },
  {
    question: "What are the common side effects of Gedarel?",
    answer:
      "Irregular bleeding is listed as very common in the first months. Common side effects (up to 1 in 10 users) include low mood, headache, dizziness, nausea, stomach pain, acne, breast pain or tenderness, no bleed in the break, painful periods, premenstrual symptoms and weight gain. Many settle within about three months.",
  },
  {
    question: "Does Gedarel have a higher blood clot risk?",
    answer:
      "Yes, slightly. Combined pills containing desogestrel, like Gedarel, are linked to about 9 to 12 blood clots per 10,000 users a year, compared with about 5 to 7 for levonorgestrel pills such as Microgynon and about 2 in non-users. Your pharmacist checks your risk factors before supplying any combined pill.",
  },
  {
    question: "What if I miss a Gedarel pill?",
    answer:
      "If you are less than 12 hours late, take it as soon as you remember and carry on as normal: the leaflet says protection is not reduced. If you are more than 12 hours late, protection may be reduced. Follow the leaflet's advice for that week of the pack, or use our missed pill tool or ask a pharmacist.",
  },
];

const tocItems = [
  { id: "what-is-it", label: "What is Gedarel?" },
  { id: "strengths", label: "Gedarel 20/150 vs 30/150" },
  { id: "same-as", label: "Gedarel, Marvelon and Mercilon" },
  { id: "how-to-take", label: "How to take it" },
  { id: "missed-pill", label: "Missed or late pills" },
  { id: "side-effects", label: "Side effects" },
  { id: "blood-clots", label: "Blood clot risk" },
  { id: "who-can-take", label: "Who can take it?" },
  { id: "getting-it", label: "How to get Gedarel free" },
];

const tableHead = "text-left px-3 py-2.5 font-semibold text-foreground border-b border-border";
const tableCell = "px-3 py-2 text-foreground/85 border-b border-border/50";
const tableFirst = "px-3 py-2 font-medium text-foreground border-b border-border/50";

const GedarelGuide = () => {
  return (
    <GuideLayout
      title="Gedarel: What You Need to Know"
      ogImage="/og/guides/gedarel.png"
      metaTitle="Gedarel 20/150 and 30/150: Side Effects and Free NHS Supply"
      metaDescription="Gedarel 20/150 and 30/150 explained: how they differ, the same medicine as Marvelon and Mercilon, side effects by frequency, clot risk and free NHS supply."
      canonicalPath="/guides/gedarel"
      nhsSupplyTitle="Getting Gedarel free on the NHS"
      nhsSupplyText="Gedarel is free on the NHS if it is suitable for you. A GPhC-registered pharmacist can supply combined pills through the NHS Pharmacy Contraception Service to people aged 16 to 49. A recent blood pressure reading is needed, and the pharmacist checks your clot risk factors carefully because desogestrel combined pills carry a slightly higher risk than levonorgestrel pills. Supply depends on that assessment."
      nhsSupplyTo="/get-the-pill-online/combined-pill/"
      nhsSupplyLabel="How to get the combined pill free on the NHS"
      breadcrumbTitle="Gedarel"
      date="October 2026"
      directAnswer="Gedarel is a combined contraceptive pill containing ethinylestradiol and desogestrel. Gedarel 30/150 has 30 micrograms of oestrogen and Gedarel 20/150 has 20 micrograms; both have 150 micrograms of desogestrel.|You take it for 21 days, then have a 7 day break. It is the same medicine as Marvelon (30/150) or Mercilon (20/150), and it is free on the NHS if a pharmacist confirms it suits you."
      quickFacts={[
        "Combined pill: ethinylestradiol 20 or 30 micrograms plus desogestrel 150 micrograms.",
        "21 days on, 7 days off. Over 99% effective with perfect use.",
        "Slightly higher clot risk than levonorgestrel pills: 9 to 12 per 10,000 users a year.",
        "Free on the NHS for ages 16 to 49 if suitable. Blood pressure check needed.",
      ]}
      tocItems={tocItems}
      faqItems={faqItems}
      ctaTitle="Ask about Gedarel at a free consultation"
      ctaDescription="Book a free phone consultation with a pharmacist. If a combined pill is right for you, it is posted in discreet packaging within 1 to 3 working days."
      ctaButtonText="Book a free NHS pharmacist consultation"
      ctaSubtext="No GP appointment needed. Supply depends on the pharmacist's assessment."
      references={[
        { text: "EMC. Gedarel 30/150 microgram film-coated tablets. Summary of Product Characteristics.", url: "https://www.medicines.org.uk/emc/product/14653/smpc" },
        { text: "EMC. Gedarel 20/150 microgram film-coated tablets. Summary of Product Characteristics.", url: "https://www.medicines.org.uk/emc/product/14652/smpc" },
        { text: "EMC. Gedarel 30/150 microgram film-coated tablets. Patient Information Leaflet.", url: "https://www.medicines.org.uk/emc/product/14653/pil" },
        { text: "FSRH. Combined Hormonal Contraception Clinical Guideline, January 2019 (amended October 2023).", url: "https://www.cosrh.org/Public/Documents/fsrh-guideline-combined-hormonal-contraception" },
        { text: "NHS England. Community Pharmacy Advanced Service Specification: NHS Pharmacy Contraception Service. Version 3.0, October 2025.", url: "https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" },
        { text: "NHS. Combined pill.", url: "https://www.nhs.uk/contraception/methods-of-contraception/combined-pill/" },
      ]}
      relatedGuides={[
        { to: "/guides/what-is-the-combined-pill/", label: "What is the combined pill?" },
        { to: "/guides/marvelon/", label: "Marvelon" },
        { to: "/guides/microgynon/", label: "Microgynon 30" },
        { to: "/guides/yasmin/", label: "Yasmin" },
        { to: "/guides/contraceptive-pill-names/", label: "Contraceptive pill names" },
        { to: "/guides/pill-and-blood-clots/", label: "The pill and blood clots" },
        { to: "/guides/combined-pill-vs-mini-pill/", label: "Combined pill vs mini pill" },
        { to: "/guides/side-effects-of-the-pill/", label: "Side effects of the pill" },
        { to: "/guides/which-contraceptive-pill-is-right-for-me/", label: "Which contraceptive pill is right for me?" },
      ]}
    >
      <h2 id="what-is-it">What is Gedarel?</h2>
      <p>
        Gedarel is a combined contraceptive pill containing two hormones: ethinylestradiol, a form of oestrogen, and desogestrel, a progestogen. It is made by Gedeon Richter and comes in two strengths, Gedarel 30/150 and Gedarel 20/150 (<a href="https://www.medicines.org.uk/emc/product/14653/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>).
      </p>
      <p>
        Like other combined pills, it works mainly by stopping your ovaries releasing an egg each month. It also thickens the mucus at the cervix and thins the womb lining. Taken correctly it is more than 99% effective, and around 91% with typical use, when pills are sometimes missed or taken late (<a href="https://www.nhs.uk/contraception/methods-of-contraception/combined-pill/" target="_blank" rel="noopener noreferrer">NHS</a>). For the wider picture, read our guide to <Link to="/guides/what-is-the-combined-pill/">the combined pill</Link>.
      </p>

      <h2 id="strengths">What is the difference between Gedarel 20/150 and 30/150?</h2>
      <p>
        Gedarel 20/150 and Gedarel 30/150 differ only in their oestrogen dose. The first number is the micrograms of ethinylestradiol in each tablet and the second is the micrograms of desogestrel. Both strengths are taken the same way and have the same missed pill rules (<a href="https://www.medicines.org.uk/emc/product/14652/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC, Gedarel 20/150</a>).
      </p>

      <div className="overflow-x-auto my-6 -mx-2">
        <table className="w-full text-sm border-collapse border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-secondary/60">
              <th className={tableHead}>Strength</th>
              <th className={tableHead}>Ethinylestradiol</th>
              <th className={tableHead}>Desogestrel</th>
              <th className={tableHead}>How you take it</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Gedarel 30/150", "30 micrograms", "150 micrograms", "21 days, then a 7 day break"],
              ["Gedarel 20/150", "20 micrograms", "150 micrograms", "21 days, then a 7 day break"],
            ].map(([a, b, c, d], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className={tableFirst}>{a}</td>
                <td className={tableCell}>{b}</td>
                <td className={tableCell}>{c}</td>
                <td className={tableCell}>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        Both strengths list the same side effects at the same frequencies in their product information. Which one you are offered depends on your history and on what you have taken before, so your pharmacist will go through this with you rather than you choosing on dose alone.
      </p>

      <h2 id="same-as">Is Gedarel the same as Marvelon and Mercilon?</h2>
      <p>
        Gedarel is the same medicine as Marvelon and Mercilon at matching strengths. Gedarel 30/150 has the same hormones and doses as <Link to="/guides/marvelon/">Marvelon</Link>, and Gedarel 20/150 matches Mercilon. Our guide to <Link to="/guides/contraceptive-pill-names/">contraceptive pill names</Link> groups every UK brand this way.
      </p>
      <p>
        "If your box changes from Marvelon to Gedarel, the hormones and doses inside are the same," says Usama Ali, MPharm (GPhC: 2224726), pharmacist at Contraception Direct. "What matters is that the strength matches. If the numbers on the pack are different from what you had before, ask your pharmacist."
      </p>

      <h2 id="how-to-take">How do you take Gedarel?</h2>
      <p>
        You take one Gedarel tablet a day for 21 days, then have a 7 day break with no tablets. A withdrawal bleed usually starts 2 to 3 days after the last tablet (<a href="https://www.medicines.org.uk/emc/product/14653/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). Start the next pack after 7 days, even if you are still bleeding.
      </p>
      <p>
        If you start on day 1 of your period, you are protected straight away. The product information also allows a start on days 2 to 5, with condoms for the first 7 days. Starting at other times, or switching from another method, has its own rules, and your pharmacist will tell you exactly when to start.
      </p>

      <h2 id="missed-pill">What if you miss or are late with a Gedarel pill?</h2>
      <p>
        If you are less than 12 hours late with a Gedarel pill, the leaflet says protection is not reduced. Take the late pill as soon as you remember, even if that means two in one day, and carry on as normal (<a href="https://www.medicines.org.uk/emc/product/14653/pil" target="_blank" rel="noopener noreferrer">EMC patient leaflet</a>).
      </p>
      <p>
        More than 12 hours late, protection may be reduced. Take the last missed pill, use condoms for the next 7 days and follow the leaflet's advice for that week of the pack. National FSRH guidance on missed combined pills is set out differently, so if you are unsure, use our <Link to="/missed-pill/">missed pill tool</Link> or ask a pharmacist. Being sick within 3 to 4 hours of a pill counts as missing it.
      </p>

      <h2 id="side-effects">What are the side effects of Gedarel?</h2>
      <p>
        Irregular bleeding is the only side effect listed as very common for Gedarel, meaning more than 1 in 10 users, and it is most likely in the first months (<a href="https://www.medicines.org.uk/emc/product/14653/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). Changes in bleeding are usually only assessed after about three cycles. The table groups the listed effects by how often they are reported.
      </p>

      <div className="overflow-x-auto my-6 -mx-2">
        <table className="w-full text-sm border-collapse border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-secondary/60">
              <th className={tableHead}>How often</th>
              <th className={tableHead}>Side effects listed for Gedarel 20/150 and 30/150</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Very common (more than 1 in 10 users)", "Irregular bleeding"],
              ["Common (up to 1 in 10 users)", "Low or changed mood, nervousness, headache, dizziness, nausea, stomach pain, acne, breast pain or tenderness, no bleed in the break, painful periods, premenstrual symptoms, weight gain"],
              ["Uncommon (up to 1 in 100 users)", "Fluid retention, lower sex drive, migraine, high blood pressure, being sick, diarrhoea, rash, hives, breast enlargement"],
              ["Rare (up to 1 in 1,000 users)", "Allergic reactions, higher sex drive, contact lens discomfort, blood clots in a vein or artery, skin reactions, brown patches on the skin (chloasma), vaginal or breast discharge, weight loss"],
            ].map(([a, b], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className={tableFirst}>{a}</td>
                <td className={tableCell}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        If side effects carry on past about three months, speak to a pharmacist rather than stopping on your own. Another combined pill or a <Link to="/guides/what-is-the-mini-pill/">mini pill</Link> may suit you better. Our guide to <Link to="/guides/side-effects-of-the-pill/">side effects of the pill</Link> covers what is normal early on.
      </p>

      <h2 id="blood-clots">What is the blood clot risk with Gedarel?</h2>
      <p>
        Gedarel carries a slightly higher blood clot risk than levonorgestrel combined pills. About 9 to 12 in every 10,000 people using a desogestrel combined pill develop a clot in a vein each year, compared with about 5 to 7 for levonorgestrel pills such as <Link to="/guides/microgynon/">Microgynon</Link> and about 2 for people not using hormonal contraception (<a href="https://www.cosrh.org/Public/Documents/fsrh-guideline-combined-hormonal-contraception" target="_blank" rel="noopener noreferrer">FSRH, 2023</a>).
      </p>
      <p>
        The overall risk is still low, and your pharmacist checks your personal risk factors first. Get urgent help, by calling 999 or going to A&E, if you have painful swelling in one leg, sudden chest pain or breathlessness, a severe or unusual headache, sudden changes in your vision, or weakness on one side. Read more in <Link to="/guides/pill-and-blood-clots/">the pill and blood clots</Link>.
      </p>

      <h2 id="who-can-take">Who can take Gedarel?</h2>
      <p>
        Gedarel can be supplied to people aged 16 to 49 through the NHS Pharmacy Contraception Service if the pharmacist finds it suitable. A recent blood pressure reading is needed for any combined pill, and the pharmacist asks about your health, medicines, smoking and family history (<a href="https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" target="_blank" rel="noopener noreferrer">NHS England PCS specification</a>).
      </p>
      <p>
        It is not suitable if you have had a blood clot, have migraine with aura, have severe high blood pressure, have certain liver conditions, or take some hepatitis C medicines (<a href="https://www.medicines.org.uk/emc/product/14653/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). If a combined pill is not right for you, the pharmacist will explain why and suggest another option, such as the mini pill, or a GP or sexual health clinic. The pill does not protect against sexually transmitted infections.
      </p>

      <h2 id="getting-it">How do you get Gedarel free?</h2>
      <p>
        Gedarel is free on the NHS from your GP, a sexual health clinic or a pharmacy offering the NHS Pharmacy Contraception Service. With <Link to="/get-the-pill-online/">Contraception Direct</Link>, you book a free phone consultation, your pharmacist checks your suitability, and if a combined pill suits you it is posted in plain packaging, usually arriving within 1 to 3 working days.
      </p>
      <p>
        The brand you receive can depend on stock, but the strength will match what the pharmacist agrees with you. Not sure which pill to ask for? Read <Link to="/guides/which-contraceptive-pill-is-right-for-me/">which contraceptive pill is right for me</Link>.
      </p>
    </GuideLayout>
  );
};

export default GedarelGuide;
